import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getAdminTimesheets, generateTimesheets, lockTimesheet, unlockTimesheet,
  hideTimesheet, unhideTimesheet,
  getAdminInvoices, generateInvoices, markInvoicePaid, markInvoiceUnpaid,
  addInvoiceExtra, removeInvoiceExtra,
  downloadInvoicePdf, getPayrollPreview,
} from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Sprout, ChevronLeft, ChevronRight, Loader2, FileText, DollarSign,
  Lock, Unlock, Download, CheckCircle, XCircle, User, LogOut, RefreshCw,
  Clock, MoreHorizontal, Printer, Eye, EyeOff, Plus, Trash2, PlusCircle
} from 'lucide-react';

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function shiftMonth(m, delta) {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(y, mo - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonth(m) {
  const [y, mo] = m.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(mo) - 1]} ${y}`;
}

export default function AdminTimesheets() {
  const { user, logout } = useAuth();
  const [month, setMonth] = useState(currentMonth());
  const [timesheets, setTimesheets] = useState({ timesheets: [], stats: {} });
  const [invoices, setInvoices] = useState({ invoices: [], stats: {} });
  const [payroll, setPayroll] = useState({ preview: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [activeView, setActiveView] = useState('timesheets');
  const [showHidden, setShowHidden] = useState(false);

  // Extra income dialog
  const [extraDialog, setExtraDialog] = useState(null); // invoice object or null
  const [extraForm, setExtraForm] = useState({ description: '', amount: '' });
  const [addingExtra, setAddingExtra] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tsRes, invRes, payRes] = await Promise.all([
        getAdminTimesheets(month), getAdminInvoices(month), getPayrollPreview(month),
      ]);
      setTimesheets(tsRes.data);
      setInvoices(invRes.data);
      setPayroll(payRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleGenerateTimesheets = async () => {
    setGenerating('timesheets');
    try {
      const res = await generateTimesheets(month);
      toast.success(res.data.message);
      loadData();
    } catch (err) {
      toast.error('Failed to generate timesheets');
    } finally {
      setGenerating(null);
    }
  };

  const handleLock = async (id) => {
    try {
      await lockTimesheet(id);
      toast.success('Timesheet locked');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to lock');
    }
  };

  const handleUnlock = async (id) => {
    try {
      await unlockTimesheet(id);
      toast.success('Timesheet unlocked');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlock');
    }
  };

  const handleHide = async (id) => {
    try {
      await hideTimesheet(id);
      toast.success('Timesheet hidden');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to hide');
    }
  };

  const handleUnhide = async (id) => {
    try {
      await unhideTimesheet(id);
      toast.success('Timesheet visible again');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unhide');
    }
  };

  const handleGenerateInvoices = async () => {
    setGenerating('invoices');
    try {
      const res = await generateInvoices(month);
      toast.success(res.data.message);
      loadData();
    } catch (err) {
      toast.error('Failed to generate invoices');
    } finally {
      setGenerating(null);
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      await markInvoicePaid(id);
      toast.success('Marked as paid');
      loadData();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const handleMarkUnpaid = async (id) => {
    try {
      await markInvoiceUnpaid(id);
      toast.success('Marked as unpaid');
      loadData();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const handleDownload = async (id, num) => {
    try {
      await downloadInvoicePdf(id, num);
    } catch (err) {
      toast.error('Failed to download');
    }
  };

  const openExtraDialog = (inv) => {
    setExtraDialog(inv);
    setExtraForm({ description: '', amount: '' });
  };

  const handleAddExtra = async () => {
    if (!extraForm.description.trim()) {
      toast.error('Please enter a description');
      return;
    }
    if (!extraForm.amount || isNaN(parseFloat(extraForm.amount))) {
      toast.error('Please enter a valid amount');
      return;
    }
    setAddingExtra(true);
    try {
      await addInvoiceExtra(extraDialog.id, {
        description: extraForm.description.trim(),
        amount: parseFloat(extraForm.amount),
      });
      toast.success('Extra item added to invoice');
      setExtraForm({ description: '', amount: '' });
      loadData();
      // Refresh dialog data
      const invRes = await getAdminInvoices(month);
      const updated = invRes.data.invoices.find(i => i.id === extraDialog.id);
      if (updated) setExtraDialog(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add extra');
    } finally {
      setAddingExtra(false);
    }
  };

  const handleRemoveExtra = async (extraId) => {
    try {
      await removeInvoiceExtra(extraDialog.id, extraId);
      toast.success('Extra item removed');
      loadData();
      const invRes = await getAdminInvoices(month);
      const updated = invRes.data.invoices.find(i => i.id === extraDialog.id);
      if (updated) setExtraDialog(updated);
      else setExtraDialog(null);
    } catch (err) {
      toast.error('Failed to remove extra');
    }
  };

  const tsStats = timesheets.stats || {};
  const invStats = invoices.stats || {};
  const payStats = payroll.stats || {};

  const allTimesheets = timesheets.timesheets || [];
  const visibleTimesheets = showHidden ? allTimesheets : allTimesheets.filter(ts => !ts.hidden);
  const hiddenCount = allTimesheets.filter(ts => ts.hidden).length;

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#0A0A0A] rounded-xl flex items-center justify-center"><Sprout className="w-5 h-5 text-white" /></div>
              <h1 className="text-lg font-bold text-[#111827] tracking-tight">Timesheets & Invoices</h1>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/admin"><Button variant="outline" size="sm" className="text-xs"><ChevronLeft className="w-3 h-3 mr-1" /> Dashboard</Button></Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="text-xs"><User className="w-3 h-3 mr-1" /> {user?.name}</Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild><Link to="/profile">Profile</Link></DropdownMenuItem>
                  <DropdownMenuItem onClick={logout}><LogOut className="w-3 h-3 mr-2" />Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Month Nav */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="prev-month"><ChevronLeft className="w-4 h-4" /></Button>
            <div className="text-center px-3">
              <p className="text-sm font-semibold text-[#111827]" data-testid="month-display">{formatMonth(month)}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="next-month"><ChevronRight className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => setMonth(currentMonth())} className="text-xs text-[#002FA7]">Current</Button>
          </div>
        </div>

        {/* View Tabs */}
        <div className="flex gap-1 mb-6 p-1 bg-gray-100 rounded-lg w-fit" data-testid="ts-tabs">
          {[
            { id: 'timesheets', label: 'Timesheets', icon: Clock },
            { id: 'invoices', label: 'Invoices', icon: FileText },
            { id: 'payroll', label: 'Payroll Preview', icon: DollarSign },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveView(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeView === tab.id ? 'bg-white text-[#111827] shadow-sm' : 'text-[#4B5563] hover:text-[#111827]'}`}
              data-testid={`tab-${tab.id}`}>
              <tab.icon className="w-3.5 h-3.5" />{tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div>
        ) : (
          <>
            {activeView === 'timesheets' && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <div className="flex gap-3">
                    <Card className="border border-black/10"><CardContent className="p-3 text-center"><p className="text-xl font-bold">{tsStats.total_tutors || 0}</p><p className="text-[10px] text-[#4B5563]">Tutors</p></CardContent></Card>
                    <Card className="border border-black/10"><CardContent className="p-3 text-center"><p className="text-xl font-bold">{tsStats.total_hours || 0}h</p><p className="text-[10px] text-[#4B5563]">Total Hours</p></CardContent></Card>
                    <Card className="border border-black/10"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-[#002FA7]">&pound;{(tsStats.total_amount || 0).toFixed(2)}</p><p className="text-[10px] text-[#4B5563]">Total Amount</p></CardContent></Card>
                  </div>
                  <div className="flex items-center gap-2">
                    {hiddenCount > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowHidden(!showHidden)}
                        className={`text-xs ${showHidden ? 'bg-amber-50 border-amber-200 text-amber-700' : ''}`}
                        data-testid="toggle-hidden-timesheets"
                      >
                        {showHidden ? <Eye className="w-3.5 h-3.5 mr-1" /> : <EyeOff className="w-3.5 h-3.5 mr-1" />}
                        {showHidden ? `Showing All (${hiddenCount} hidden)` : `${hiddenCount} Hidden`}
                      </Button>
                    )}
                    <Button size="sm" onClick={handleGenerateTimesheets} disabled={generating === 'timesheets'} className="bg-[#002FA7] hover:bg-[#002482]" data-testid="generate-timesheets">
                      {generating === 'timesheets' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Generate Timesheets
                    </Button>
                  </div>
                </div>
                <Card className="border border-black/10">
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tutor</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Entries</TableHead>
                          <TableHead>Hours</TableHead>
                          <TableHead>Rate</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="w-24">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {visibleTimesheets.map(ts => (
                          <TableRow key={ts.id} className={ts.hidden ? 'opacity-60 bg-gray-50/50' : ''} data-testid={`ts-row-${ts.id}`}>
                            <TableCell className="font-medium text-sm">{ts.tutor_name}</TableCell>
                            <TableCell><Badge className="bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20 text-xs">{ts.tutor_type === 'main_tutor' ? 'Main' : 'Standby'}</Badge></TableCell>
                            <TableCell className="text-sm">{ts.entries?.length || 0}</TableCell>
                            <TableCell className="text-sm font-medium">{ts.total_hours}h</TableCell>
                            <TableCell className="text-sm">&pound;{(ts.hourly_rate || 0).toFixed(2)}</TableCell>
                            <TableCell className="text-sm font-bold">&pound;{(ts.total_amount || 0).toFixed(2)}</TableCell>
                            <TableCell>
                              {ts.status === 'locked'
                                ? <Badge className="bg-gray-100 text-gray-700">Locked</Badge>
                                : <Badge className="bg-green-50 text-green-700 border-green-200">Active</Badge>}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-0.5">
                                {ts.status === 'active' ? (
                                  <Button variant="ghost" size="sm" onClick={() => handleLock(ts.id)} title="Lock timesheet" data-testid={`lock-${ts.id}`}>
                                    <Lock className="w-3.5 h-3.5 text-[#4B5563]" />
                                  </Button>
                                ) : (
                                  <Button variant="ghost" size="sm" onClick={() => handleUnlock(ts.id)} title="Unlock timesheet" data-testid={`unlock-${ts.id}`}>
                                    <Unlock className="w-3.5 h-3.5 text-amber-600" />
                                  </Button>
                                )}
                                {ts.hidden ? (
                                  <Button variant="ghost" size="sm" onClick={() => handleUnhide(ts.id)} title="Unhide timesheet" data-testid={`unhide-${ts.id}`}>
                                    <Eye className="w-3.5 h-3.5 text-amber-600" />
                                  </Button>
                                ) : (
                                  <Button variant="ghost" size="sm" onClick={() => handleHide(ts.id)} title="Hide timesheet" data-testid={`hide-${ts.id}`}>
                                    <EyeOff className="w-3.5 h-3.5 text-[#9CA3AF]" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {visibleTimesheets.length === 0 && (
                          <TableRow><TableCell colSpan={8} className="text-center text-sm text-[#9CA3AF] py-8">
                            {hiddenCount > 0 && !showHidden
                              ? `All timesheets are hidden. Click "${hiddenCount} Hidden" to view them.`
                              : 'No timesheets for this month. Click "Generate Timesheets" to create them.'}
                          </TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </>
            )}

            {activeView === 'invoices' && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <div className="flex gap-3">
                    <Card className="border border-black/10"><CardContent className="p-3 text-center"><p className="text-xl font-bold">{invStats.total_invoices || 0}</p><p className="text-[10px] text-[#4B5563]">Invoices</p></CardContent></Card>
                    <Card className="border border-black/10"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-[#10B981]">{invStats.paid || 0}</p><p className="text-[10px] text-[#4B5563]">Paid</p></CardContent></Card>
                    <Card className="border border-black/10"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-[#EF4444]">{invStats.unpaid || 0}</p><p className="text-[10px] text-[#4B5563]">Unpaid</p></CardContent></Card>
                    <Card className="border border-black/10"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-[#002FA7]">&pound;{(invStats.total_amount || 0).toFixed(2)}</p><p className="text-[10px] text-[#4B5563]">Total</p></CardContent></Card>
                  </div>
                  <Button size="sm" onClick={handleGenerateInvoices} disabled={generating === 'invoices'} className="bg-[#002FA7] hover:bg-[#002482]" data-testid="generate-invoices">
                    {generating === 'invoices' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileText className="w-3.5 h-3.5 mr-1" />} Generate Invoices
                  </Button>
                </div>
                <Card className="border border-black/10">
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Invoice #</TableHead>
                          <TableHead>Tutor</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Hours</TableHead>
                          <TableHead>Rate</TableHead>
                          <TableHead>Extras</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="w-20"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(invoices.invoices || []).map(inv => {
                          const extrasTotal = (inv.extras || []).reduce((s, e) => s + e.amount, 0);
                          return (
                            <TableRow key={inv.id} data-testid={`inv-row-${inv.id}`}>
                              <TableCell className="font-mono text-sm font-medium">{inv.invoice_number}</TableCell>
                              <TableCell className="text-sm">{inv.tutor_name}</TableCell>
                              <TableCell><Badge className="bg-[#002FA7]/10 text-[#002FA7] text-xs">{inv.tutor_type === 'main_tutor' ? 'Main' : 'Standby'}</Badge></TableCell>
                              <TableCell className="text-sm">{inv.total_hours}h</TableCell>
                              <TableCell className="text-sm">&pound;{(inv.hourly_rate || 0).toFixed(2)}</TableCell>
                              <TableCell className="text-sm">
                                {extrasTotal !== 0 ? (
                                  <span className={`font-medium ${extrasTotal > 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                                    {extrasTotal > 0 ? '+' : ''}&pound;{extrasTotal.toFixed(2)}
                                  </span>
                                ) : (
                                  <span className="text-[#9CA3AF]">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-sm font-bold">&pound;{(inv.total_amount || 0).toFixed(2)}</TableCell>
                              <TableCell>
                                {inv.status === 'paid'
                                  ? <Badge className="bg-green-50 text-green-700 border-green-200">Paid</Badge>
                                  : <Badge className="bg-red-50 text-red-700 border-red-200">Unpaid</Badge>}
                              </TableCell>
                              <TableCell>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => openExtraDialog(inv)} data-testid={`add-extra-${inv.id}`}>
                                      <PlusCircle className="w-3 h-3 mr-2" /> Add Extra / Other Income
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleDownload(inv.id, inv.invoice_number)}><Download className="w-3 h-3 mr-2" /> Download PDF</DropdownMenuItem>
                                    {inv.status === 'unpaid'
                                      ? <DropdownMenuItem onClick={() => handleMarkPaid(inv.id)}><CheckCircle className="w-3 h-3 mr-2" /> Mark Paid</DropdownMenuItem>
                                      : <DropdownMenuItem onClick={() => handleMarkUnpaid(inv.id)}><XCircle className="w-3 h-3 mr-2" /> Mark Unpaid</DropdownMenuItem>
                                    }
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {(invoices.invoices || []).length === 0 && (
                          <TableRow><TableCell colSpan={9} className="text-center text-sm text-[#9CA3AF] py-8">No invoices for this month. Lock timesheets first, then generate invoices.</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </>
            )}

            {activeView === 'payroll' && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                  {[
                    { label: 'Tutors', value: payStats.total_tutors || 0 },
                    { label: 'Total Hours', value: `${payStats.total_hours || 0}h` },
                    { label: 'Total Payroll', value: `\u00a3${(payStats.total_payroll || 0).toFixed(2)}`, color: 'text-[#002FA7]' },
                    { label: 'Paid', value: `\u00a3${(payStats.paid || 0).toFixed(2)}`, color: 'text-[#10B981]' },
                    { label: 'Outstanding', value: `\u00a3${(payStats.outstanding || 0).toFixed(2)}`, color: payStats.outstanding > 0 ? 'text-[#EF4444]' : 'text-[#10B981]' },
                  ].map((s, i) => (
                    <Card key={i} className="border border-black/10" data-testid={`payroll-stat-${i}`}>
                      <CardContent className="p-4 text-center">
                        <p className={`text-xl font-bold ${s.color || 'text-[#111827]'}`}>{s.value}</p>
                        <p className="text-xs text-[#4B5563]">{s.label}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <Card className="border border-black/10" data-testid="payroll-table">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2"><Printer className="w-4 h-4" /> Payroll Summary — {formatMonth(month)}</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tutor</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Rate</TableHead>
                          <TableHead>Hours</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Timesheet</TableHead>
                          <TableHead>Invoice</TableHead>
                          <TableHead>Payment</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(payroll.preview || []).map((p, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-medium text-sm">{p.tutor_name}</TableCell>
                            <TableCell><Badge className="text-xs bg-[#002FA7]/10 text-[#002FA7]">{p.tutor_type === 'main_tutor' ? 'Main' : 'Standby'}</Badge></TableCell>
                            <TableCell className="text-sm">&pound;{(p.hourly_rate || 0).toFixed(2)}</TableCell>
                            <TableCell className="text-sm">{p.total_hours}h</TableCell>
                            <TableCell className="text-sm font-bold">&pound;{(p.total_amount || 0).toFixed(2)}</TableCell>
                            <TableCell>
                              {p.timesheet_status === 'locked'
                                ? <Badge className="bg-gray-100 text-gray-700 text-xs">Locked</Badge>
                                : <Badge className="bg-green-50 text-green-700 text-xs">Active</Badge>}
                            </TableCell>
                            <TableCell className="text-xs font-mono">{p.invoice_number || '-'}</TableCell>
                            <TableCell>
                              {p.invoice_status === 'paid' ? <Badge className="bg-green-50 text-green-700 text-xs">Paid</Badge> :
                               p.invoice_status === 'unpaid' ? <Badge className="bg-red-50 text-red-700 text-xs">Unpaid</Badge> :
                               <span className="text-xs text-[#9CA3AF]">-</span>}
                            </TableCell>
                          </TableRow>
                        ))}
                        {(payroll.preview || []).length === 0 && (
                          <TableRow><TableCell colSpan={8} className="text-center text-sm text-[#9CA3AF] py-8">No payroll data for this month.</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}
      </div>

      {/* Add Extra / Other Income Dialog */}
      <Dialog open={!!extraDialog} onOpenChange={() => setExtraDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-base">Add Extra / Other Income</DialogTitle>
            <p className="text-xs text-[#4B5563] mt-1">
              Invoice: <strong>{extraDialog?.invoice_number}</strong> — {extraDialog?.tutor_name}
            </p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Existing extras */}
            {extraDialog?.extras?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-[#374151] mb-2">Current Extra Items</p>
                <div className="space-y-1.5">
                  {extraDialog.extras.map((ex) => (
                    <div key={ex.id} className="flex items-center justify-between p-2 bg-[#F9FAFB] rounded-md border border-gray-100">
                      <div>
                        <p className="text-sm text-[#111827]">{ex.description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-semibold ${ex.amount >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                          {ex.amount >= 0 ? '+' : ''}&pound;{ex.amount.toFixed(2)}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveExtra(ex.id)}
                          className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                          data-testid={`remove-extra-${ex.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add new */}
            <div className="border-t border-gray-100 pt-3">
              <p className="text-xs font-semibold text-[#374151] mb-2">Add New Item</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Description</Label>
                  <Input
                    value={extraForm.description}
                    onChange={(e) => setExtraForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="e.g. Travel allowance, Bonus, Extra tutoring"
                    className="mt-1"
                    data-testid="extra-description-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Amount (&pound;)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={extraForm.amount}
                    onChange={(e) => setExtraForm(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="e.g. 15.00 or -5.00 for deduction"
                    className="mt-1"
                    data-testid="extra-amount-input"
                  />
                  <p className="text-[10px] text-[#9CA3AF] mt-1">Use negative for deductions</p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setExtraDialog(null)}>Close</Button>
            <Button
              size="sm"
              onClick={handleAddExtra}
              disabled={addingExtra || !extraForm.description.trim() || !extraForm.amount}
              className="bg-[#002FA7] hover:bg-[#002482]"
              data-testid="submit-extra-btn"
            >
              {addingExtra ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
              Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
